/* eslint-disable react-refresh/only-export-components */
import React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/utils/cn'

const inputVariants = cva(
  'flex w-full rounded-md px-3 py-2 text-sm transition-all file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-tutara-gray disabled:cursor-not-allowed disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-tutara-charcoal dark:text-gray-200 focus:border-tutara-turquoise focus:outline-none focus:ring-2 focus:ring-tutara-turquoise/20',
        filled:
          'border-2 border-transparent bg-gray-50 dark:bg-gray-800 text-tutara-charcoal dark:text-gray-200 focus:border-tutara-turquoise focus:bg-white dark:focus:bg-gray-700 focus:outline-none',
        underline:
          'border-0 border-b-2 border-gray-200 dark:border-gray-700 rounded-none px-0 text-tutara-charcoal dark:text-gray-200 focus:border-tutara-teal focus:outline-none',
      },
      size: {
        sm: 'h-8 text-xs',
        md: 'h-10',
        lg: 'h-12 text-base',
      },
      state: {
        default: '',
        error: 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
        success: 'border-tutara-turquoise focus:border-tutara-turquoise',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'md',
      state: 'default',
    },
  }
)

export interface TutaraInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'>,
    VariantProps<typeof inputVariants> {
  label?: string
  error?: string
  hint?: string
  leftIcon?: React.ReactNode
  rightIcon?: React.ReactNode
}

const TutaraInput = React.forwardRef<HTMLInputElement, TutaraInputProps>(
  (
    {
      className,
      variant,
      size,
      state,
      label,
      error,
      hint,
      leftIcon,
      rightIcon,
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || props.name
    const hasError = error || state === 'error'

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-tutara-charcoal dark:text-gray-200 mb-1"
          >
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-tutara-teal dark:text-teal-300">
              {leftIcon}
            </div>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              inputVariants({ variant, size, state: hasError ? 'error' : state }),
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              className
            )}
            {...props}
          />
          {rightIcon && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-tutara-teal dark:text-teal-300">
              {rightIcon}
            </div>
          )}
        </div>
        {error && (
          <p className="mt-1 text-xs text-red-500">{error}</p>
        )}
        {hint && !error && (
          <p className="mt-1 text-xs text-tutara-gray">{hint}</p>
        )}
      </div>
    )
  }
)

TutaraInput.displayName = 'TutaraInput'

export { TutaraInput, inputVariants }